import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Play, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";

const QUARTILES = [["q25", 25], ["q50", 50], ["q75", 75]];

function hasAudioTrack(v) {
  if (typeof v.mozHasAudio === "boolean") return v.mozHasAudio;
  if (v.audioTracks) return v.audioTracks.length > 0;
  if (typeof v.webkitAudioDecodedByteCount === "number" && v.currentTime > 1) return v.webkitAudioDecodedByteCount > 0;
  return null;
}

function useQuartiles(adId, pct) {
  const fired = useRef({});
  useEffect(() => {
    QUARTILES.forEach(([k, th]) => {
      if (!fired.current[k] && pct >= th) { fired.current[k] = true; api.post("/adx/track", { ad_id: adId, type: k }).catch(() => {}); }
    });
  }, [adId, pct]);
}

function VideoCreative({ ad, required, policy, onProgress, onDone, onError }) {
  const ref = useRef(null);
  const watched = useRef(0);
  const lastT = useRef(null);
  const done = useRef(false);
  const target = useRef(required);
  const [needTap, setNeedTap] = useState(false);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(true);
  const [audio, setAudio] = useState(null);
  const [portrait, setPortrait] = useState(true);

  const tryPlay = async (withSound) => {
    const v = ref.current; if (!v) return;
    v.muted = !withSound; setMuted(!withSound);
    try { await v.play(); setNeedTap(false); } catch (e) { if (e?.name === "NotAllowedError") { v.pause(); setNeedTap(true); } }
  };

  useEffect(() => {
    const v = ref.current;
    const onVis = () => { if (document.hidden) v.pause(); else if (!done.current && !needTap) v.play().catch(() => {}); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [needTap]);

  const onMeta = () => {
    const v = ref.current;
    setPortrait(v.videoHeight >= v.videoWidth);
    if (policy === "full_length" && v.duration && v.duration < required) target.current = v.duration;
    tryPlay(true);
  };
  const onTime = () => {
    const v = ref.current; const t = v.currentTime;
    if (lastT.current != null && !v.paused) {
      const d = t - lastT.current;
      if (d > 0 && d < 1.5) watched.current += d;
    }
    lastT.current = t;
    if (audio == null) { const a = hasAudioTrack(v); if (a != null) setAudio(a); }
    onProgress(Math.min(watched.current, target.current), target.current);
    if (!done.current && watched.current >= target.current - 0.05) {
      done.current = true; v.pause();
      onDone(watched.current, v.duration || 0);
    }
  };
  const onEnded = () => {
    if (done.current) return;
    if (policy === "full_length") { done.current = true; onDone(watched.current, ref.current.duration || 0); return; }
    lastT.current = 0; ref.current.currentTime = 0; ref.current.play().catch(() => {});
  };
  const toggleMute = () => { const v = ref.current; v.muted = !v.muted; setMuted(v.muted); if (v.paused && !done.current) v.play().catch(() => {}); };

  return (
    <div className={`relative mx-auto bg-black rounded-2xl overflow-hidden border border-border ${portrait ? "w-full max-w-[min(100%,calc(68vh*9/16))]" : "w-full"}`} data-testid="ad-video-wrap">
      <video ref={ref} src={mediaUrl(ad.media_url)} playsInline preload="auto" disablePictureInPicture controlsList="nodownload noplaybackrate nofullscreen"
        onLoadedMetadata={onMeta} onTimeUpdate={onTime} onEnded={onEnded} onError={() => onError("This video can't be played on your device.")}
        onPlaying={() => { setPlaying(true); setBuffering(false); }} onPause={() => setPlaying(false)} onWaiting={() => setBuffering(true)}
        className={`block w-full object-contain bg-black ${portrait ? "aspect-[9/16] max-h-[68vh]" : "aspect-video"}`} data-testid="ad-video" />
      {buffering && !needTap && <div className="absolute inset-0 grid place-items-center pointer-events-none"><Loader2 className="h-8 w-8 animate-spin text-white/80" /></div>}
      {needTap && (
        <button onClick={() => tryPlay(true)} data-testid="ad-tap-to-play" className="absolute inset-0 grid place-items-center bg-black/55 text-white">
          <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground font-semibold"><Play className="h-4 w-4 fill-current" /> Tap to Play with Sound</span>
        </button>
      )}
      {!needTap && (
        <button onClick={toggleMute} data-testid="ad-mute-toggle" aria-label={muted ? "Unmute" : "Mute"}
          className="absolute bottom-3 right-3 h-10 w-10 grid place-items-center rounded-full bg-black/60 text-white backdrop-blur hover:bg-black/80">
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
      )}
      {!needTap && (
        <span className="absolute bottom-3 left-3 text-[11px] px-2 py-1 rounded-full bg-black/60 text-white" data-testid="ad-audio-status">
          {!playing ? "Paused" : audio === false ? "No audio in this ad" : muted ? "Muted" : "Sound on"}
        </span>
      )}
    </div>
  );
}

function ImageCreative({ ad, required, onProgress, onDone }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    let watched = 0; let last = performance.now();
    const id = setInterval(() => {
      const now = performance.now();
      if (!document.hidden) watched += (now - last) / 1000;
      last = now;
      onProgress(Math.min(watched, required), required);
      if (watched >= required) { clearInterval(id); onDone(watched, 0); }
    }, 250);
    return () => clearInterval(id);
  }, [loaded]); // eslint-disable-line
  return (
    <div className="rounded-2xl overflow-hidden border border-border bg-black">
      <img src={mediaUrl(ad.media_url)} alt={ad.title} onLoad={() => setLoaded(true)} className="w-full max-h-[60vh] object-contain" data-testid="ad-image" />
    </div>
  );
}

export default function RewardedAd({ session, onUnlock, onFail }) {
  const { ad, countdown: required = 10, short_video_policy: policy = "loop" } = session;
  const [prog, setProg] = useState({ t: 0, total: required });
  const [phase, setPhase] = useState("playing");
  const [err, setErr] = useState("");
  const pct = prog.total ? (prog.t / prog.total) * 100 : 0;
  useQuartiles(ad.id, pct);

  const done = async (watched, duration) => {
    setPhase("completed");
    try { await onUnlock(watched, duration); } catch (e) { setPhase("playing"); onFail(e); }
  };
  const onError = (m) => { setErr(m); setPhase("error"); };
  const clickCta = () => { api.post(`/ads/${ad.id}/click`).catch(() => {}); window.open(ad.destination_url, "_blank", "noopener"); };
  const left = Math.max(0, Math.ceil(prog.total - prog.t));
  const props = { ad, required, policy, onProgress: (t, total) => setProg({ t, total }), onDone: done, onError };

  return (
    <div data-testid="ad-playing" className="text-center">
      <div className="flex items-center justify-between text-xs track-label text-muted-foreground mb-3">
        <span data-testid="ad-label">Advertisement</span>
        <span data-testid="ad-countdown">{phase === "completed" ? "Done" : `${left}s`}</span>
      </div>
      {phase === "error" ? (
        <div className="rounded-2xl border border-border p-8" data-testid="ad-playback-error">
          <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto" />
          <p className="text-sm mt-3">{err}</p>
          <button onClick={() => onFail(null)} className="mt-4 text-sm text-primary underline" data-testid="ad-try-another">Try another ad</button>
        </div>
      ) : ad.ad_type === "video" ? <VideoCreative {...props} /> : <ImageCreative {...props} />}
      <p className="text-xs text-muted-foreground mt-3" data-testid="ad-brand">{ad.brand_name}</p>
      <h3 className="font-display font-bold text-lg" data-testid="ad-title">{ad.title}</h3>
      {ad.description && <p className="text-sm text-muted-foreground mt-1" data-testid="ad-description">{ad.description}</p>}
      {ad.destination_url && <button onClick={clickCta} data-testid="ad-cta" className="mt-3 px-5 py-2 rounded-full border border-primary text-primary text-sm font-semibold hover:bg-primary/10 transition-colors">{ad.cta_text || "Learn More"}</button>}
      <div className="h-2 bg-secondary rounded-full overflow-hidden mt-4"><div className="h-full bg-primary transition-[width] duration-200 ease-linear" style={{ width: `${pct}%` }} data-testid="ad-progress" /></div>
      <p className="mt-2 text-sm font-medium" data-testid="ad-status-text">
        {phase === "completed" ? <span className="inline-flex items-center gap-1.5 text-primary"><CheckCircle2 className="h-4 w-4" /> Ad Completed — unlocking chapter…</span> : `Chapter unlocks in ${left}s`}
      </p>
    </div>
  );
}
